import React, { useRef } from "react"
import { Card, CardBody, Button, Row, Col } from "reactstrap"
import PropTypes from "prop-types"
import { FILES_URL } from "helpers/url_helper"
import moment from "moment"

// Helper to render the file icon matching the image style
const FileIconItem = ({ fileName }) => {
    const ext = fileName ? fileName.split('.').pop().toUpperCase() : "FILE"

    // Determine icon or text based on extension
    let iconContent = null
    const isTextType = ["XML", "TXT", "PNG", "JPG", "JPEG", "GIF"].includes(ext)

    // Style for the container (Orange/Gold box)
    const boxStyle = {
        width: "60px",
        height: "70px",
        backgroundColor: "#FFCA5C", // Gold color from image
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "8px",
        color: "#222",
        fontWeight: "900",
        fontSize: "18px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
    }

    if (ext === "PDF") {
        iconContent = <i className="bx bxs-file-pdf" style={{ fontSize: "36px" }}></i>
    } else if (ext === "DOC" || ext === "DOCX") {
        iconContent = <span style={{ fontSize: "20px" }}>DOC</span>
    } else if (ext === "XLS" || ext === "XLSX") {
        iconContent = <span style={{ fontSize: "20px" }}>XLS</span>
    } else if (isTextType) {
        iconContent = <span>{ext}</span>
    } else {
        iconContent = <i className="bx bxs-file-blank" style={{ fontSize: "32px" }}></i>
    }

    return (
        <div style={boxStyle}>
            {iconContent}
        </div>
    )
}

// Reusable Category Card with Slider
const FileCategoryCard = ({ title, files, onAdd, onDelete }) => {
    const scrollRef = useRef(null)

    const scroll = (direction) => {
        if (scrollRef.current) {
            const { current } = scrollRef
            const scrollAmount = 200
            if (direction === "left") {
                current.scrollBy({ left: -scrollAmount, behavior: "smooth" })
            } else {
                current.scrollBy({ left: scrollAmount, behavior: "smooth" })
            }
        }
    }

    return (
        <Card style={{ backgroundColor: "#383838", borderRadius: "15px", border: "none", height: "100%" }}>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 style={{ color: "#FFCA5C", fontWeight: "bold", margin: 0, fontSize: "16px" }}>{title}</h6>
                    {/* <Button
                        size="sm"
                        style={{
                            backgroundColor: "#FFCA5C",
                            color: "#000",
                            border: "none",
                            borderRadius: "20px",
                            fontSize: "11px",
                            fontWeight: "bold",
                            padding: "4px 12px"
                        }}
                        onClick={() => onAdd(title)}
                    >
                        + Add
                    </Button> */}
                </div>

                <div className="position-relative d-flex align-items-center">
                    {/* Left Arrow */}
                    <div onClick={() => scroll("left")} style={{ cursor: "pointer", zIndex: 10, marginRight: "5px", display: files && files.length > 3 ? "block" : "none" }}>
                        <i className="bx bx-chevron-left text-secondary" style={{ fontSize: "24px" }}></i>
                    </div>

                    <div
                        ref={scrollRef}
                        className="d-flex"
                        style={{
                            overflowX: "auto",
                            gap: "20px",
                            scrollbarWidth: "none", // Firefox
                            paddingBottom: "10px",
                            width: "100%",
                            scrollBehavior: "smooth"
                        }}
                    >
                        {/* Hide scrollbar for Webkit */}
                        <style>{`.d-flex::-webkit-scrollbar { display: none; }`}</style>

                        {files && files.length > 0 ? files.map((file, idx) => (
                            <div key={idx} className="d-flex flex-column align-items-center" style={{ minWidth: "90px" }}>
                                {/* Time Ago */}
                                <small className="text-muted mb-2" style={{ fontSize: "9px" }}>
                                    {moment(file.created_at).fromNow()}
                                </small>

                                {/* File Icon */}
                                <a href={`${FILES_URL}${file.file_name}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                    <FileIconItem fileName={file.file_name} />
                                </a>

                                {/* Filename */}
                                <small className="text-muted text-center text-truncate" style={{ width: "85px", fontSize: "10px", marginBottom: "5px" }}>
                                    {file.file_name}
                                </small>

                                {/* Delete Icon */}
                                <div style={{ cursor: "pointer" }} onClick={() => onDelete(file.id)}>
                                    <i className="bx bxs-trash" style={{ color: "#FF5858", fontSize: "14px" }}></i>
                                </div>
                            </div>
                        )) : (
                            <div className="text-muted text-center w-100 py-4" style={{ fontSize: "12px" }}>
                                No files
                            </div>
                        )}
                    </div>

                    {/* Right Arrow */}
                    <div onClick={() => scroll("right")} style={{ cursor: "pointer", zIndex: 10, marginLeft: "5px", display: files && files.length > 3 ? "block" : "none" }}>
                        <i className="bx bx-chevron-right text-secondary" style={{ fontSize: "24px" }}></i>
                    </div>
                </div>
            </CardBody>
        </Card>
    )
}

const LocationFiles = ({ location, onAddFile, onDeleteFile }) => {
    if (!location) return null;

    // Define categories mapping to location keys
    const categories = [
        { title: "Contract", key: "contract_files" },
        { title: "Amendment", key: "amendment_files" },
        { title: "Business Licence", key: "business_licence_files" },
        { title: "City Document", key: "city_document_files" },
        { title: "Receipt", key: "receipt_files" },
        { title: "Signage", key: "signage_files" },
    ]

    return (
        <Row>
            {categories.map((cat, idx) => (
                <Col md={6} xl={4} key={idx} className="mb-4">
                    <FileCategoryCard
                        title={cat.title}
                        files={location[cat.key]}
                        onAdd={() => onAddFile(cat.key)}
                        onDelete={onDeleteFile}
                    />
                </Col>
            ))}
        </Row>
    )
}

LocationFiles.propTypes = {
    location: PropTypes.object,
    onAddFile: PropTypes.func,
    onDeleteFile: PropTypes.func
}

export default LocationFiles